import { WRI_AQUEDUCT_SOURCE_LABEL } from '../../lib/wriAqueductSource';
/**
 * Data Provider Orchestrator — calls all providers in parallel,
 * caches results in Firestore, and aggregates into a unified dataset.
 */
import type { DataSource, ClimateData, GridData, ProjectIntent, TimeSeriesData, UserApiKeys } from '../../types/siteRag';
import type { ProviderGeoContext } from './types';
import { validateCoords } from './types';
import { withCache } from './cache';
import { openMeteoProvider } from './openMeteo';
import { usgsSeismicProvider, type SeismicData } from './usgsSeismic';
import { femaFloodProvider, type FloodData } from './femaFlood';
import { hifldGridProvider } from './hifldGrid';
import { nrelSolarProvider, type SolarData } from './nrelSolar';
import { nrelWindProvider, type WindData } from './nrelWind';
import { environmentalProvider, type EnvironmentalData } from './usfwsHabitat';
import { eiaElectricityProvider, type ElectricityData } from './eiaElectricity';
import { eiaNaturalGasProvider, type NaturalGasData } from './eiaNaturalGas';
import { airnowProvider, type AirQualityData } from './airnowAqi';
import { peeringDbProvider, type ConnectivityData } from './peeringDb';
import { regridParcelProvider, isRegridEnabled, type ParcelData } from './regridParcels';
import { nrcReactorProvider, type NrcReactorData } from './nrcReactors';
import { wriAqueductProvider, type WaterStressData } from './wriAqueduct';
import { siteResearchProvider, type SiteResearchData } from './siteResearch';
import { regulatoryDbProvider } from './regulatoryDb';
import { nrcAdamsProvider } from './nrcAdams';
import { siteProfileProvider } from './siteProfile';
import { epaAttainmentProvider, type EpaAttainmentData } from './epaAttainment';
import { withProviderCompanions } from './providerCompanions';
import { nrcsSoilSurveyProvider, type GeotechnicalData } from './nrcsSoilSurvey';
import { isJurisdictionScreeningEnabled, jurisdictionProvider } from './jurisdiction';
import { usgsMrdsProvider, type MineralResourcesScreenData } from './usgsMrds';
import { npsNationalRegisterProvider, type CulturalResourcesScreenData } from './npsNationalRegister';
// RD-1 (D) — refdata-only providers (no live upstream; served from the
// nationwide GCS snapshots; throw 'refdata dataset not available' → the
// permanent-unavailable classification when unseeded).
import { epaFrsProvider } from './epaFrs';
import { usgsWatersProvider } from './usgsWaters';
import { padusProtectedProvider } from './padusProtected';
import { censusAiannhProvider } from './censusAiannh';
import { effectiveWorkloadProfile, getWorkloadProfile } from '../../lib/workloadProfile';
import { bucketMidpointMw } from '../../lib/projectIntent';
import {
  isNrcsSsurgoDefaultEnabled,
  siteDiligencePublicScreensEnabled,
  shouldDispatchNrcsSsurgo,
} from '../../config/siteDiligenceProviders';
import { throwIfAborted } from '../../lib/abortSignal';

/** True when the intent's power bucket represents a ≥200 MW (large/hyperscale)
 *  project — captures the new ladder's high buckets and the legacy
 *  'over-200mw' value alike. */
function isLargePowerBucket(intent: ProjectIntent): boolean {
  return (bucketMidpointMw(intent.powerRequirement) ?? 0) >= 200;
}

export type { SeismicData } from './usgsSeismic';
export type { SolarData } from './nrelSolar';
export type { WindData } from './nrelWind';
export type { EnvironmentalData } from './usfwsHabitat';
export type { FloodData } from './femaFlood';
export type { ElectricityData } from './eiaElectricity';
export type { NaturalGasData } from './eiaNaturalGas';
export type { AirQualityData } from './airnowAqi';
export type { ConnectivityData } from './peeringDb';
export type { ParcelData } from './regridParcels';
export type { NrcReactorData } from './nrcReactors';
export type { WaterStressData } from './wriAqueduct';
export type { SiteResearchData } from './siteResearch';
export type { GeotechnicalData } from './nrcsSoilSurvey';
export type { JurisdictionEvidence } from '../../types/siteEvidence';
export type { MineralResourcesScreenData } from './usgsMrds';
export type { CulturalResourcesScreenData } from './npsNationalRegister';

export type { UserApiKeys } from '../../types/siteRag';

/**
 * PU4 — richer failure-mode taxonomy that distinguishes
 *
 *   - `transient-failure`     — timeout / 5xx / ECONNRESET / DNS blip.
 *                                Retry on next run.
 *   - `permanent-unavailable` — provider can't cover this lat/lng
 *                                (e.g. NREL outside US, EIA offshore).
 *   - `misconfigured`         — our integration is broken (rotated
 *                                upstream id, ArcGIS error envelope).
 *                                NOT a transient. Surfaces an admin-only
 *                                banner.
 *   - `auth-missing`          — PRR-4: API key env var not set in
 *                                Cloud Run secrets. The proxy stamps
 *                                `X-DA-Key-Source: none`; upstream then
 *                                401/403s. Distinct from misconfigured
 *                                so the recommendation can name the
 *                                env var directly.
 *   - `auth-invalid`          — PRR-4: API key WAS sent but upstream
 *                                rejected it (401/403 with `X-DA-Key-Source:
 *                                org|user`). Distinct from auth-missing
 *                                so the recommendation says "rotate"
 *                                instead of "set in Secret Manager".
 *   - `rate-limited`          — PRR-4: upstream returned 429. Different
 *                                fix from a transient blip (upgrade plan
 *                                / back off cadence), so it gets its own
 *                                bucket.
 *   - `skipped`               — provider was filtered out at dispatch
 *                                (selectedProviders / non-US coverage).
 *
 * Pre-PU4 the renderer collapsed all four into the same "data is
 * unavailable" copy. Today the user understands what they're looking
 * at and whether a rerun will fix it.
 *
 * The legacy `status` field stays exactly as it was so call-sites that
 * already read `status === 'failed' / 'success' / ...` keep working —
 * the richer `failureMode` field is additive and ONLY populated when
 * `status === 'failed'`.
 */
export type ProviderFailureMode =
  | 'transient-failure'
  | 'permanent-unavailable'
  | 'misconfigured'
  | 'auth-missing'
  | 'auth-invalid'
  | 'rate-limited'
  | 'skipped';

export interface ProviderStatus {
  id: string;
  name: string;
  /** R-71 — `baseline` / `unavailable` are additive; legacy `failed` checks unchanged. */
  status: 'success' | 'cached' | 'failed' | 'skipped' | 'baseline' | 'unavailable';
  error?: string;
  /**
   * PU4 — populated whenever `status === 'failed'` (and `'skipped'` to
   * carry the existing semantic via the unified enum). Mirrors EL's
   * null-handling discipline: a known failure-mode is a first-class
   * value, never coerced into a generic "failed".
   */
  failureMode?: ProviderFailureMode;
  /**
   * PRR-4 — optional env-var name that controls the provider's API key.
   * Only populated when `failureMode === 'auth-missing'` so the
   * recommendation in `assessGaps` can name the exact variable an admin
   * has to set in Cloud Run Secret Manager. Providers that don't require
   * a key (USFWS, WRI, USGS) leave this undefined.
   */
  envVar?: string;
}

/**
 * PU4 — classify a thrown provider error message into a
 * `ProviderFailureMode`. The taxonomy reads the raw error string the
 * provider threw (the only signal we get post-`Promise.allSettled`)
 * plus the provider's id for the "well-known permanent-unavailable"
 * shortcuts.
 *
 * Order matters: misconfigured patterns are matched FIRST so a
 * rotated ArcGIS id doesn't get mis-classified as transient just
 * because the underlying transport happened to throw.
 *
 * If you add a new error string to a provider, add the matcher here
 * so PU5 retry doesn't waste budget retrying permanent failures.
 */
export function classifyProviderFailure(providerId: string, errorMessage: string): ProviderFailureMode {
  const msg = errorMessage.toLowerCase();
  if (msg.includes('unsupported-study-area-geometry')) return 'permanent-unavailable';

  // ── permanent-unavailable shortcuts ──────────────────────────────
  // The dispatcher already writes `error: 'non-us-coverage'` for the
  // US-only providers when the site is outside US — that's a
  // permanent-unavailable, not a misconfiguration.
  if (msg === 'non-us-coverage' || msg.includes('non-us-coverage')) {
    return 'permanent-unavailable';
  }
  // sitespecific providers (FEMA / USFWS / Regrid) returning empty are
  // permanent for the queried lat/lng — retrying won't help.
  if (msg.includes('no data for this location') || msg.includes('no coverage') || msg.includes('no parcels found')) {
    return 'permanent-unavailable';
  }
  // RD-1 (D) — refdata-only providers (epa-frs, usgs-waters,
  // padus-protected, census-aiannh) have no live upstream: when their
  // dataset is unseeded / the engine is disabled, retrying cannot help.
  // Remediation is an ops action (seed via scripts/data-ingest/refdata/),
  // surfaced through PROVIDER_REMEDIATION.
  if (msg.includes('refdata dataset not available')) {
    return 'permanent-unavailable';
  }

  // ── auth-missing (PRR-4) ─────────────────────────────────────────
  // Matched BEFORE the generic 401/403 → misconfigured rule so the
  // provider can self-declare "key absent in env" via a dedicated
  // error string. Providers throw this when the proxy reported
  // `X-DA-Key-Source: none` AND upstream rejected the keyless request.
  if (msg.includes('api key not configured') || msg.includes('api_key not configured')) {
    return 'auth-missing';
  }

  // ── auth-invalid (PRR-4) ─────────────────────────────────────────
  // Distinct from auth-missing — the key WAS sent, upstream rejected
  // it. Recommendation: rotate the key, not "set the env var".
  if (msg.includes('api key rejected') || msg.includes('api_key rejected')) {
    return 'auth-invalid';
  }

  // ── rate-limited (PRR-4) ─────────────────────────────────────────
  // 429 = the upstream told us to back off. Different remediation
  // from a transient blip (upgrade plan / reduce cadence), so it gets
  // its own bucket.
  if (msg.includes(': 429') || msg.includes('rate limit') || msg.includes('too many requests')) {
    return 'rate-limited';
  }

  // ── misconfigured (integration-broken) patterns ──────────────────
  // WRI Aqueduct HTTP 4xx without an ArcGIS envelope still means a
  // rotated service id or bad layer — not a transient blip.
  if (providerId === 'wri-aqueduct' && /wri aqueduct: (400|403|404)/i.test(msg)) {
    return 'misconfigured';
  }
  // ArcGIS error envelope (WRI Aqueduct rotated id, generic Esri error)
  if (msg.includes('arcgis error')
    || msg.includes('invalid query parameters')
    || msg.includes('malformed arcgis response')) {
    return 'misconfigured';
  }
  // 401 / 403 — almost always a stale or unbound API key. Could be
  // upstream auth temporarily down, but the right action is "check
  // the Secret Manager binding" not "retry". Falls back here when the
  // provider didn't self-declare auth-missing/auth-invalid via a
  // specific error string.
  if (msg.includes(': 401') || msg.includes(': 403') || msg.includes('unauthorized') || msg.includes('forbidden')) {
    return 'misconfigured';
  }
  // AirNow retired its separate current ZIP/coordinate routes on 2026-09-30.
  // HTTP 410 is an adapter migration problem, not a transient provider fault.
  if (providerId === 'airnow-aqi' && /\bairnow:\s*410\b/.test(msg)) {
    return 'misconfigured';
  }
  // 404 on a known provider means the route was deprecated.
  if (msg.includes(': 404') || msg.includes('endpoint not found') || msg.includes('deprecated')) {
    return 'misconfigured';
  }
  // Non-numeric / empty-outputs on a provider that should always return
  // the field — schema drift = misconfigured.
  if (msg.includes('non-numeric') || msg.includes('returned no solar data') || msg.includes('returned no wind data')) {
    return 'misconfigured';
  }

  // ── transient-failure (the catch-all retry-worthy bucket) ────────
  // Everything else is treated as transient — that's the safest
  // default since the worst case is "we retried once and lost a
  // little budget".  Includes: ECONNRESET, ETIMEDOUT, AbortError,
  // 500 / 502 / 503 / 504, 'fetch failed', 'all sources failed'.
  void providerId; // reserved for future per-provider overrides
  return 'transient-failure';
}

/**
 * PRR-4 — canonical env-var name per provider id, used to populate
 * `ProviderStatus.envVar` so the failure-mode-aware recommendation in
 * `assessGaps` can name the variable an admin has to set.
 *
 * Aligns with `SERVER_API_KEYS` in server.ts. Providers that don't
 * require a key (USFWS, WRI, USGS, FEMA, NOAA, HIFLD) are absent here
 * and `envVar` stays undefined.
 */
export const PROVIDER_ENV_VAR: Record<string, string> = {
  'nrel-solar': 'NREL_API_KEY',
  'nrel-wind': 'NREL_API_KEY',
  'eia-electricity': 'EIA_API_KEY',
  'eia-natural-gas': 'EIA_API_KEY',
  'airnow-aqi': 'AIRNOW_API_KEY',
  'site-research': 'GEMINI_API_KEY',
};

export interface AggregatedSiteData {
  climate: ClimateData | null;
  grid: GridData | null;
  flood: FloodData | null;
  seismic: SeismicData | null;
  solar: SolarData | null;
  wind: WindData | null;
  environmental: EnvironmentalData | null;
  electricity: ElectricityData | null;
  /** Resolved by the `eia-natural-gas` provider — state citygate / industrial
   *  price (or Henry Hub fallback) keyed off (lat, lng). Optional so legacy
   *  callers / SEDs that omit the provider compile cleanly (v1.2.1 HR-5 P2-4). */
  naturalGas?: NaturalGasData | null;
  airQuality: AirQualityData | null;
  connectivity: ConnectivityData | null;
  parcel: ParcelData | null;
  nrcReactors: NrcReactorData | null;
  waterStress: WaterStressData | null;
  siteResearch: SiteResearchData | null;
  epaAttainment?: EpaAttainmentData | null;
  /** USDA SSURGO regional soil screening. Never represents a borehole or
   * site-specific professional geotechnical investigation. */
  geotechnical?: GeotechnicalData | null;
  /** Resolved by the synthetic `regulatory-db` provider — state-stance
   *  lookup keyed off (lat, lng). Optional so legacy callers that omit
   *  the provider compile cleanly. */
  regulatory?: import('./regulatoryDb').RegulatoryEvidenceData | null;
  /** Curated municipal-limit / ETJ geographic screen. */
  jurisdiction?: import('../../types/siteEvidence').JurisdictionEvidence | null;
  /** Resolved by the synthetic `nrc-adams` provider — curated NRC
   *  document set. Optional for the same reason. */
  adamsDocuments?: import('./nrcAdams').AdamsDocument[] | null;
  /** Resolved by the synthetic `site-profile` provider — centroid-
   *  only derivation. The orchestrator overlays full boundary
   *  geometry when `site.siteBoundary` is present. */
  siteProfileFromCentroid?: import('../../types/siteEvidence').SiteProfileEvidence | null;
  /** Public occurrence screening only; mineral ownership remains document-only. */
  mineralResources?: MineralResourcesScreenData | null;
  /** Unrestricted listed-property screening only; clearance remains document-only. */
  culturalResources?: CulturalResourcesScreenData | null;
  /** RD-1 (D) — refdata-only structured screens (secondary contributions
   *  into `environmental`) + the real waterResource payload. */
  regulatedFacilities?: import('../../types/siteEvidence').EnvironmentalRegulatedFacilities | null;
  protectedAreas?: import('../../types/siteEvidence').EnvironmentalProtectedAreas | null;
  tribalLands?: import('../../types/siteEvidence').EnvironmentalTribalLands | null;
  waterResource?: import('../../types/siteEvidence').WaterResourceEvidence | null;
  references: DataSource[];
  providerStatuses: ProviderStatus[];
}

/**
 * Per-provider remediation classification — used by SiteRagReportView's
 * Report Quality card to group failures by the action that fixes them.
 *
 *  - `config`       → admin needs to configure something (env var / API key
 *                     binding). The CTA routes to the System Health card.
 *  - `transient`    → rate-limit, timeout, or upstream blip. Retry via
 *                     rerun-analysis is the right answer.
 *  - `sitespecific` → provider returned no data for THIS lat/lng (e.g. EIA
 *                     has no rate data for offshore coordinates). Expected;
 *                     no fix.
 *
 * Keep this list aligned with PROVIDER_CATALOG below.
 */
export type RemediationType = 'config' | 'transient' | 'sitespecific';

export const PROVIDER_REMEDIATION: Record<string, RemediationType> = {
  // Server-managed API keys — config issue when missing.
  'nrel-solar': 'config',
  'nrel-wind': 'config',
  'eia-electricity': 'config',
  'eia-natural-gas': 'config',
  'airnow-aqi': 'config',
  // Gemini-backed AI research is degraded-mode; the dedicated banner
  // already surfaces Gemini failure. We still classify it as `config` for
  // the rare case the banner isn't shown — the fix is GEMINI_API_KEY in
  // Secret Manager, not a rerun.
  'site-research': 'config',
  // No-key government APIs — failures are usually transient (CDN blip, DNS,
  // brief outage) or site-specific (no parcel coverage in this state).
  'fema-flood': 'sitespecific',
  'usfws-env': 'sitespecific',
  'usgs-seismic': 'transient',
  'hifld-grid': 'transient',
  'open-meteo': 'transient',
  'peeringdb-ix': 'transient',
  'nrc-reactors': 'transient',
  'wri-aqueduct': 'transient',
  'nrcs-soil-survey': 'sitespecific',
  'jurisdiction-boundaries': 'sitespecific',
  'usgs-mrds': 'sitespecific',
  'nps-nrhp': 'sitespecific',
  // RD-1 (D) — refdata-only providers. Deliberately 'config' (not
  // 'sitespecific'): the dominant failure is an UNSEEDED dataset, and the
  // fix is an ops/admin action (run the seed CLI), not a rerun.
  'epa-frs': 'config',
  'usgs-waters': 'config',
  'padus-protected': 'config',
  'census-aiannh': 'config',
};

/**
 * Provider IDs that only return data inside the United States. The list itself
 * lives in `src/config/usOnlyProviders.ts` (P1.8 — a dependency-free leaf the
 * enqueue-time territory guard shares with this dispatcher); re-exported here
 * unchanged for the existing importers.
 */
import { US_ONLY_PROVIDER_IDS } from '../../config/usOnlyProviders';
export { US_ONLY_PROVIDER_IDS };

/** Quick US-bounding-box check. Cheap, deterministic, no network.
 *  Covers the contiguous lower-48 plus Alaska + Hawaii + DC. The
 *  primary use is the dispatcher; non-US sites flow through the
 *  `global-degraded` coverage mode. */
export function isUSCoordinate(lat: number, lng: number): boolean {
  // CONUS
  if (lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66) return true;
  // Alaska
  if (lat >= 51 && lat <= 72 && lng >= -180 && lng <= -129) return true;
  // Hawaii
  if (lat >= 18 && lat <= 23 && lng >= -161 && lng <= -154) return true;
  return false;
}

/**
 * Per-provider tooltip surfaced on the failed-provider chip. Generic when no
 * specific entry exists. Kept terse — the chip has limited room.
 */
export const PROVIDER_TOOLTIP: Record<string, string> = {
  'nrel-solar': 'Needs the NREL_API_KEY environment variable. Free key at developer.nrel.gov/signup. Ask an admin to add it via the System Health card.',
  'nrel-wind': 'Needs the NREL_API_KEY environment variable. Free key at developer.nrel.gov/signup. Ask an admin to add it via the System Health card.',
  'eia-electricity': 'Needs the EIA_API_KEY environment variable. Free key at eia.gov/opendata/register.php. Ask an admin to add it via the System Health card.',
  'eia-natural-gas': 'Needs the EIA_API_KEY environment variable (same key as EIA Electricity). Free key at eia.gov/opendata/register.php. Ask an admin to add it via the System Health card.',
  'airnow-aqi': 'Needs the AIRNOW_API_KEY environment variable. Free key at docs.airnowapi.org/account/request. Ask an admin to add it via the System Health card.',
  'site-research': 'Powered by the Gemini API. Needs GEMINI_API_KEY in Secret Manager. The degraded-mode banner above already covers this when shown.',
  'regrid-parcels': 'Regrid integration not enabled — set VITE_REGRID_ENABLED=true and provide a regridApiKey, or upload a survey / parcel CSV under Site Documents to provide parcel evidence.',
  'jurisdiction-boundaries': 'Jurisdiction screening is disabled in this deployment or no curated boundary is available. Enable VITE_JURISDICTION_SCREENING_ENABLED and rebuild.',
};

/** Provider metadata for UI display and recommendation engine. */
export const PROVIDER_CATALOG = [
  // Free — no key required
  { id: 'open-meteo', name: 'Open-Meteo Climate', description: '10-year historical weather normals', fields: 'Temp, Wet Bulb, Humidity, Water Stress', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'climate' },
  { id: 'usgs-seismic', name: 'USGS Seismic (ASCE 7-22)', description: 'Seismic design parameters', fields: 'PGA, SDS, SD1, Design Category', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'seismic' },
  { id: 'fema-flood', name: 'FEMA Flood Zones (NFHL)', description: 'Parcel-intersected National Flood Hazard Layer', fields: 'Exclusive Zone Acreage, 1% / 0.2% Annual Chance, SFHA, Floodway, Unmapped Area', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'nrcs-soil-survey', name: 'USDA NRCS Web Soil Survey (SSURGO)', description: 'Regional soil map-unit screening; not a site investigation', fields: 'Map Unit, Texture, Inferred USCS, Drainage, Bedrock / Seasonal Water Table', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'geotechnical' },
  { id: 'jurisdiction-boundaries', name: 'Municipal Limits and ETJ Screening', description: 'Curated local boundaries with Census city-limit fallback; never infers ETJ buffers', fields: 'City Limit, ETJ, Parcel Overlap, Coverage Status', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'regulatory' },
  { id: 'hifld-grid', name: 'HIFLD Grid Infrastructure', description: 'Electric substations & transmission', fields: 'Distance, Voltage, Capacity', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'grid' },
  { id: 'usfws-env', name: 'USFWS / NWI Environmental', description: 'Critical habitat & wetlands', fields: 'ESA Species, Wetland Type, Acreage', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'peeringdb-ix', name: 'PeeringDB IX Proximity', description: 'Internet Exchange & DC locations', fields: 'Nearest IX, Distance, DC Count', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'connectivity' },
  { id: 'nrc-reactors', name: 'NRC Reactor Status', description: 'Operating reactor proximity & status', fields: 'Nearest Reactor, Distance, Power Level, Nuclear Maturity', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'nuclear' },
  { id: 'wri-aqueduct', name: WRI_AQUEDUCT_SOURCE_LABEL, description: 'Historical basin water-stress screening from a third-party 2.1 mirror', fields: 'Water Stress Score, Category, Overall Risk', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'climate' },
  { id: 'site-research', name: 'AI Site-Specific Research', description: 'Location-specific water rights, tribal lands, hazards, permitting, precedents', fields: 'Water Rights, Tribal Consultation, Local Hazards, Utility, Permitting, Precedents', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'regulatory' },
  // Paid — feature-flagged via VITE_REGRID_ENABLED. Always surfaces in the
  // UI's provider-status panel so admins can see whether it's available;
  // the dispatch layer checks the flag before actually firing a request.
  { id: 'regrid-parcels', name: 'Regrid Parcel Data', description: 'Parcel boundaries, zoning, ownership', fields: 'Parcel ID, Acreage, Zoning, Owner, Land Use', requiresKey: true, keyField: 'regridApiKey', signupUrl: 'https://regrid.com/api', pricing: 'Paid', category: 'parcel' },
  // Server-managed keys — injected by proxy, no user key needed
  { id: 'nrel-solar', name: 'NREL Solar Resource', description: 'Solar irradiance data', fields: 'GHI, DNI', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'renewable' },
  { id: 'nrel-wind', name: 'NREL Wind Toolkit', description: 'Wind speed at hub heights', fields: 'Wind Speed 80m/100m, Capacity Factor', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'renewable' },
  { id: 'eia-electricity', name: 'EIA Electricity Rates', description: 'Industrial retail electricity pricing', fields: 'Rate (cents/kWh), State', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'grid' },
  { id: 'eia-natural-gas', name: 'EIA Natural Gas Prices', description: 'State citygate / industrial gas price + Henry Hub benchmark — grounds gas-BTM fuel cost', fields: 'Price ($/MMBtu), State, Henry Hub', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'grid' },
  { id: 'airnow-aqi', name: 'AirNow Air Quality (EPA)', description: 'Real-time AQI for permitting', fields: 'AQI, PM2.5, Pollutant', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'epa-attainment', name: 'EPA NAAQS Designations (Green Book NAYRO)', description: 'Bundled EPA Green Book NAYRO table — current nonattainment and maintenance designations for every NAAQS by county FIPS; revoked standards excluded. Part-county nonattainment areas are checked at the site point against the live EPA NonattainmentAreas MapServer (gispub.epa.gov)', fields: 'FIPS, designation status, pollutants, ozone classification, designations, part-county site containment, coverage', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'regulatory' },
  { id: 'usgs-mrds', name: 'USGS Mineral Resource Data System', description: 'Public mineral occurrence screening; never mineral-title or ownership evidence', fields: 'Parcel/1 mi/5 mi occurrence counts, nearest occurrence, commodities, truncation', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'siting' },
  // RD-1 (D) — refdata-only providers served from the nationwide GCS
  // snapshots (scripts/data-ingest/refdata/). No live upstream.
  { id: 'epa-frs', name: 'EPA FRS Regulated Facilities', description: 'Regulated-facility proximity screen from the EPA Facility Registry Service national snapshot', fields: '1 mi / 3 mi facility counts, per-program counts, nearest facility', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'usgs-waters', name: 'USGS Watersheds & Aquifers', description: 'WBD HUC8/10/12 watershed identity + principal / sole-source aquifer overlays', fields: 'HUC codes + names, principal aquifer, sole-source aquifer', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'padus-protected', name: 'PAD-US Protected Areas', description: 'Structured protected-area screen (fee/easement/designation) from the USGS PAD-US snapshot', fields: 'On-parcel + 1 mi units, manager, designation, GAP status, nearest distance', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'census-aiannh', name: 'Census AIANNH Tribal Lands', description: 'American Indian / Alaska Native / Native Hawaiian area overlap + proximity screen', fields: 'On-parcel flag, overlapping areas, nearest distance', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  { id: 'nps-nrhp', name: 'NPS National Register', description: 'Unrestricted public listed-property screening; not archaeological clearance or Section 106 compliance', fields: 'Parcel/1 mi/5 mi listed-property counts, nearest distance, truncation', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'environmental' },
  // Synthetic providers added by Content-Source Fixes Prompt 5 so the
  // SED top-up dispatcher can refresh `regulatory` / `adamsDocuments` /
  // `siteProfile` sections like any other provider. None require a
  // key; each is a thin wrapper around local helpers.
  { id: 'regulatory-db', name: 'State Regulatory Stance', description: 'Curated US state nuclear stance dictionary', fields: 'Stance, Notes', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'regulatory' },
  { id: 'nrc-adams', name: 'NRC ADAMS Document Index', description: 'NRC regulatory document search (curated query)', fields: 'Accession, Title, Date, Type', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'regulatory' },
  { id: 'site-profile', name: 'Site Profile (boundary derivations)', description: 'Centroid-derived elevation; full geometry from siteBoundary', fields: 'Acres, Perimeter, Elevation', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'siting' },
  // LL pack (Live Literature Retrieval) — three orchestrator-only
  // providers. They appear in the PROVIDER_CATALOG so the System
  // Health card + provider-status UI surfaces them, but they are NOT
  // wired into `providerMap` below. The synthetic
  // `external-literature` orchestrator (built in LL5) is what
  // `dispatchProviders` invokes; these three are called from inside
  // that wrapper. PubMed throughput can be raised with a free NCBI
  // API key (`NCBI_API_KEY`, server-side) — undocumented when absent so the
  // pack is fully usable out-of-the-box.
  { id: 'crossref', name: 'Crossref', description: 'Peer-reviewed publication metadata + DOIs', fields: 'DOI, Title, Authors, Year, Abstract, Citation Count', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'literature' },
  { id: 'arxiv', name: 'arXiv', description: 'Open-access preprints (physics / CS / quant-bio)', fields: 'arXiv ID, Title, Authors, Year, Abstract', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'literature' },
  { id: 'pubmed', name: 'PubMed', description: 'Biomedical / environmental-health citation index (NCBI E-utilities)', fields: 'PMID, DOI, Title, Authors, Year, Abstract', requiresKey: false, keyField: null, signupUrl: 'https://www.ncbi.nlm.nih.gov/account/', pricing: 'Free', category: 'literature' },
  { id: 'external-literature', name: 'Live Literature Retrieval', description: 'Crossref + arXiv + PubMed orchestrator (LL5)', fields: 'Aggregated LiteratureEntry[]', requiresKey: false, keyField: null, signupUrl: null, pricing: 'Free', category: 'literature' },
] as const;

/**
 * Recommend which databases to use based on project intent and location.
 */
export function recommendProviders(intent: ProjectIntent, lat: number): string[] {
  const rec: string[] = [];
  const push = (id: string) => {
    if (!rec.includes(id)) rec.push(id);
  };

  // Always recommended
  push('open-meteo');
  push('fema-flood');
  if (isNrcsSsurgoDefaultEnabled()) push('nrcs-soil-survey');
  if (siteDiligencePublicScreensEnabled()) {
    push('usgs-mrds');
    push('nps-nrhp');
  }
  if (isJurisdictionScreeningEnabled()) {
    push('jurisdiction-boundaries');
    // Jurisdiction is nested inside RegulatoryEvidence; fetch the keyed
    // state stance in the same sweep so the typed section can carry both.
    push('regulatory-db');
  }

  // WRI Aqueduct — in FAST_PROVIDER_IDS and feasibility requiredSedSections;
  // basin stress gates wet/hybrid cooling feasibility.
  push('wri-aqueduct');

  // Grid is essential for > 10MW
  if (intent.powerRequirement !== 'under-10mw') push('hifld-grid');

  // Seismic for nuclear, large projects, or seismic zones
  if (intent.nuclearInterest !== 'no' || isLargePowerBucket(intent) || (lat > 32 && lat < 48)) {
    push('usgs-seismic');
  }

  // Environmental for nuclear or sustainability
  if (intent.nuclearInterest !== 'no' || intent.priority === 'sustainability') {
    push('usfws-env');
  }

  // NRC reactor proximity when nuclear is not explicitly rejected
  if (intent.nuclearInterest !== 'no') {
    push('nrc-reactors');
  }

  // Workload-profile-driven extras. Reads `extraDataProviders` from the
  // canonical WORKLOAD_PROFILES table — adding a new profile or wiring a new
  // provider needs zero changes here, just a row edit. Each ID must match a
  // PROVIDER_CATALOG entry below; unknown IDs are filtered out so a typo
  // in the table doesn't crash the orchestrator.
  const profile = getWorkloadProfile(effectiveWorkloadProfile(intent));
  const validIds = new Set<string>(PROVIDER_CATALOG.map(p => p.id));
  for (const extraId of profile.extraDataProviders) {
    if (validIds.has(extraId)) push(extraId);
  }

  // Data-center workloads need tariff + fibre context for BTM reports
  if (profile.scoringUseCase === 'ai_dc' || profile.id === 'enterprise' || profile.id === 'colocation') {
    push('eia-electricity');
  }

  // Enterprise DC — dual-homed transit still needs IX proximity for blind-spots honesty
  if (profile.id === 'enterprise') {
    push('peeringdb-ix');
  }

  // Renewables for sustainability or non-nuclear
  if (intent.priority === 'sustainability' || intent.nuclearInterest === 'no') {
    push('nrel-solar');
    push('nrel-wind');
  }

  // Electricity pricing for cost-focused or large projects
  if (intent.priority === 'cost' || isLargePowerBucket(intent)) {
    push('eia-electricity');
  }

  // v1.2.1 HR-5 P2-4 — natural-gas price for any gas pathway (grounds the
  // gas-BTM fuel cost). A gas phase powerSource / hybrid mix or a granular
  // recip / gas-turbine component pulls it in; nuclear / renewables-only intents
  // skip it (no irrelevant-axis cost).
  {
    const gasSources = new Set(['gas-cc', 'gas-sc', 'hybrid']);
    const gasComponents = new Set(['recip', 'gas-turbine']);
    const hasGas = (intent.phases ?? []).some(p =>
      gasSources.has(p.powerSource) ||
      (p.powerSourceConfig?.components ?? []).some(c => gasComponents.has(c)),
    );
    if (hasGas) push('eia-natural-gas');
  }

  // Air quality for non-trivial projects (permitting / PSD relevance)
  if (intent.powerRequirement !== 'under-10mw') {
    push('airnow-aqi');
  }

  // Regrid parcel data — only surfaced as a recommendation when the
  // deployment has the feature flag on. Without the flag, the SED's
  // `parcel` slot stays {status:'unavailable'} and the UI tooltips
  // guide the user to upload a survey/parcel CSV instead.
  if (isRegridEnabled()) {
    push('regrid-parcels');
  }

  return rec;
}

/**
 * Coordinates handed to `fetchAllSiteData`.
 *
 *  - `lat` / `lng` — the geocoded-address point (Google Places lookup of the
 *    typed address string). The universal fallback; always present.
 *  - `centroid`    — `centroidOf(siteBoundary)`, when a boundary polygon is in
 *    scope. Optional; defaults to `(lat, lng)` so legacy sites with no drawn
 *    boundary keep querying the address (no behaviour change).
 *  - `boundary`    — the drawn boundary polygon (≥3 points), when one exists.
 *    Used by the spatially-aware providers (FEMA multi-point flood sweep, USFWS
 *    buffered-envelope species query).
 */
export interface SiteCoordinates {
  studyAreaScope?: import('../../types/siteStudyArea').SiteStudyAreaScope;
  lat: number;
  lng: number;
  centroid?: { lat: number; lng: number };
  boundary?: { lat: number; lng: number }[];
  boundaries?: { id: string; polygon: { lat: number; lng: number }[]; holes?: { lat: number; lng: number }[][] }[];
}

/**
 * Provider IDs that query at the boundary centroid instead of the geocoded
 * address (CA3). Everything not listed — and every legacy site with no drawn
 * boundary, where `geo.centroid === geo.address` anyway — queries the address.
 *
 * Grew one entry per CA3 commit. The boundary-aware providers (`fema-flood`,
 * `usfws-env`) also appear here so their *single-point* fallback (legacy
 * sites / sub-3-point geometry) lands on the centroid; their multi-point /
 * buffered behaviour (CA4) reads `geo.boundary` directly.
 */
export const CENTROID_QUERY_PROVIDER_IDS: ReadonlySet<string> = new Set<string>([
  'fema-flood',       // CA3 — sharp flood-zone boundaries; centroid (then CA4 sweep)
  'nrcs-soil-survey', // point-in-map-unit lookup; use the actual parcel centroid
  'usfws-env',        // CA3 — habitat/wetland polygons; centroid (then CA4 envelope)
  'open-meteo',       // CA3 — climate; centroid so it agrees with the elevation point
  'airnow-aqi',       // CA3 — the "nearest station within 50 mi" resolves off the query point
  'hifld-grid',       // CA3 — substation/transmission distance is measured from the parcel
  'nrc-reactors',     // CA3 — reactor proximity distance is measured from the parcel
  'regrid-parcels',   // CA3 — parcel boundaries are sharp; the centroid hits the right parcel
  'site-research',    // CA3 — the AI keys its research off the coords embedded in the prompt
  'peeringdb-ix',     // CA3 — IX/DC proximity distance is measured from the parcel
  'usgs-seismic',     // CA3 — slow gradient; centroid for coordinate-drift consistency
  'nrel-solar',       // CA3 — slow gradient; centroid for coordinate-drift consistency
  'nrel-wind',        // CA3 — slow gradient; centroid for coordinate-drift consistency
  'wri-aqueduct',     // CA3 — basin-level; centroid (and the right side of a basin divide)
  'eia-electricity',  // CA3 — FCC block → state; centroid (and the right side of a state line)
  'eia-natural-gas',  // CA3 — FCC block → state; centroid (and the right side of a state line)
  'regulatory-db',    // CA3 — FCC block → state; centroid (and the right side of a state line)
  'nrc-adams',        // CA3 — FCC block → state; centroid (and the right side of a state line)
  'site-profile',     // CA3 — the "centroid-only" synthetic derivation should query the centroid
  'jurisdiction-boundaries', // exact parcel intersections; centroid selects curated registry
  'usgs-mrds',        // occurrence distances use parcel geometry around the centroid
  'nps-nrhp',        // listed-property distances use parcel geometry around the centroid
  'epa-attainment',   // point-in-county lookup: the parcel centroid, not the mailbox, picks the county (and its Green Book designation)
]);

export { siteDiligencePublicScreensEnabled } from '../../config/siteDiligenceProviders';

/**
 * Per-provider query-coordinate resolver.
 *
 * Returns the coordinate a provider should query at — the boundary centroid
 * for the providers in `CENTROID_QUERY_PROVIDER_IDS`, the geocoded address for
 * everything else (and every legacy site with no drawn boundary, where the two
 * coincide). The cache key follows whatever this returns.
 */
export function effectiveQueryCoord(
  providerId: string,
  geo: ProviderGeoContext,
): { lat: number; lng: number } {
  return CENTROID_QUERY_PROVIDER_IDS.has(providerId) ? geo.centroid : geo.address;
}

/**
 * Fetch data from selected providers in parallel.
 *
 * Accepts a `SiteCoordinates` object: the geocoded address (`lat`/`lng`) plus
 * an optional boundary `centroid` and `boundary` polygon. The orchestrator
 * resolves the effective query coordinate per provider via `effectiveQueryCoord`
 * and hands every provider a `ProviderGeoContext` so the spatially-aware ones
 * (FEMA flood, USFWS species) can reach the boundary polygon.
 */
export async function fetchAllSiteData(
  coords: SiteCoordinates,
  userApiKeys?: UserApiKeys,
  selectedProviders?: string[],
  /** PH-A1.7 — Optional report-type hint. Currently threaded into
   *  `siteResearchProvider` so its AI prompt framing matches the
   *  downstream report (environmental, water, test-fit, …) instead of
   *  the hardcoded "nuclear power + data center" framing. Undefined
   *  for callers that don't yet have a report in scope (e.g. SED
   *  bootstrap) — provider falls back to the feasibility-style default. */
  reportType?: string,
  abortSignal?: AbortSignal,
): Promise<AggregatedSiteData> {
  throwIfAborted(abortSignal);
  const { lat, lng } = coords;
  // Centroid defaults to the geocoded address — legacy sites with no drawn
  // boundary keep their existing behaviour. `boundary` stays undefined for them.
  const geo: ProviderGeoContext = {
    studyAreaScope: coords.studyAreaScope,
    address: { lat, lng },
    centroid: coords.centroid ?? { lat, lng },
    boundary: coords.boundary && coords.boundary.length >= 3 ? coords.boundary : undefined,
    boundaries: coords.boundaries?.filter(p => p.polygon.length >= 3),
    reportType,
    signal: abortSignal,
  };
  try {
    validateCoords(lat, lng);
  } catch (e) {
    // Invalid coords — return empty data instead of crashing
    return { climate: null, grid: null, flood: null, seismic: null, solar: null, wind: null, environmental: null, electricity: null, naturalGas: null, airQuality: null, connectivity: null, parcel: null, nrcReactors: null, waterStress: null, siteResearch: null, geotechnical: null, jurisdiction: null, references: [{ field: 'coordinates', source: `Coordinate validation failed: ${(e as Error).message}`, retrievedAt: new Date().toISOString() }], providerStatuses: [] };
  }

  const references: DataSource[] = [];
  const providerStatuses: ProviderStatus[] = [];
  // Providers whose evidence nests in a section another provider compiles run
  // together even for direct/scoped calls (jurisdiction → regulatory-db, the
  // EPA designation → alongside usfws-env), so recompiling a section never
  // silently drops a companion's evidence. See providerCompanions.ts.
  const effectiveSelectedProviders = selectedProviders
    ? withProviderCompanions(selectedProviders)
    : undefined;

  // Map provider IDs to instances + keys
  const providerMap: { provider: any; key: string | undefined }[] = [
    { provider: openMeteoProvider, key: undefined },
    { provider: usgsSeismicProvider, key: undefined },
    { provider: femaFloodProvider, key: undefined },
    { provider: nrcsSoilSurveyProvider, key: undefined },
    { provider: hifldGridProvider, key: undefined },
    { provider: environmentalProvider, key: undefined },
    { provider: peeringDbProvider, key: undefined },
    { provider: nrcReactorProvider, key: undefined },
    { provider: wriAqueductProvider, key: undefined },
    { provider: siteResearchProvider, key: undefined },
    // Keys for NREL, EIA, AirNow are injected server-side by the proxy — no client keys needed
    { provider: nrelSolarProvider, key: undefined },
    { provider: nrelWindProvider, key: undefined },
    { provider: eiaElectricityProvider, key: undefined },
    // v1.2.1 HR-5 P2-4 — natural-gas price (server-side EIA key, same as electricity).
    { provider: eiaNaturalGasProvider, key: undefined },
    { provider: airnowProvider, key: undefined },
    { provider: epaAttainmentProvider, key: undefined },
    // Regrid (paid). The provider itself rejects calls when the
    // VITE_REGRID_ENABLED flag is off (regridParcels.ts:isRegridEnabled),
    // so we can safely register it here unconditionally; gating happens
    // inside the provider so the orchestrator's try/catch flow records
    // "unavailable: regrid_disabled" in the SED without 401'ing.
    { provider: regridParcelProvider, key: userApiKeys?.regridApiKey },
    // Synthetic providers (Content-Source Fixes Prompt 5). Register
    // them here so `selectedProviders` filter recognises them and the
    // top-up dispatcher can route a section refresh through the same
    // mechanism as every other provider. Keyless — pass undefined.
    { provider: regulatoryDbProvider, key: undefined },
    { provider: nrcAdamsProvider, key: undefined },
    { provider: siteProfileProvider, key: undefined },
    ...(isJurisdictionScreeningEnabled()
      ? [{ provider: jurisdictionProvider, key: undefined }]
      : []),
    ...(
      siteDiligencePublicScreensEnabled() ||
      selectedProviders?.some(id => id === 'usgs-mrds' || id === 'nps-nrhp')
        ? [
          { provider: usgsMrdsProvider, key: undefined },
          { provider: npsNationalRegisterProvider, key: undefined },
        ]
        : []
    ),
    // RD-1 (D) — refdata-only providers, registered unconditionally: when
    // their dataset is unseeded they throw the canonical
    // 'refdata dataset not available' string → permanent-unavailable, and
    // the negative cache makes the probe nearly free.
    { provider: epaFrsProvider, key: undefined },
    { provider: usgsWatersProvider, key: undefined },
    { provider: padusProtectedProvider, key: undefined },
    { provider: censusAiannhProvider, key: undefined },
  ];

  // Filter by selection. An explicit provider list is an authorized rerun /
  // acceptance invocation and may call SSURGO even while its production
  // automatic-default gate is off. An unfiltered automatic sweep honors the
  // rollout policy.
  const nrcsSsurgoAllowed = shouldDispatchNrcsSsurgo(effectiveSelectedProviders);
  const calls = (effectiveSelectedProviders
    ? providerMap.filter(c => effectiveSelectedProviders.includes(c.provider.id))
    : providerMap)
    .filter(c => c.provider.id !== 'nrcs-soil-survey' || nrcsSsurgoAllowed);

  for (const [id, name] of [
    ['nrcs-soil-survey', 'USDA NRCS SSURGO'],
    ['usgs-mrds', 'USGS Mineral Resource Data System'],
    ['nps-nrhp', 'NPS National Register'],
  ] as const) {
    if (!calls.some(call => call.provider.id === id)
      && !providerMap.some(call => call.provider.id === id && effectiveSelectedProviders)) {
      providerStatuses.push({ id, name, status: 'skipped', failureMode: 'skipped',
        error: 'Automatic public screening is disabled by deployment configuration; no lookup was attempted.' });
    }
  }

  // Track skipped
  if (effectiveSelectedProviders) {
    for (const c of providerMap) {
      if (!effectiveSelectedProviders.includes(c.provider.id)) {
        // PU4 — `failureMode: 'skipped'` mirrors the existing `status: 'skipped'`
        // through the richer enum so renderers that read `failureMode` see a
        // unified taxonomy.
        providerStatuses.push({
          id: c.provider.id, name: c.provider.name,
          status: 'skipped', failureMode: 'skipped',
        });
      }
    }
  }

  // Content-Source Fixes Prompt 14 — drop US-only providers when the
  // site is outside US coverage. Their slot in the SED carries
  // `unavailable: non-us-coverage` so the report appendix can show why
  // a section is empty instead of going dark silently.
  const coverageIsUS = isUSCoordinate(lat, lng);
  const callsAfterCoverage = coverageIsUS
    ? calls
    : calls.filter(c => !(US_ONLY_PROVIDER_IDS as readonly string[]).includes(c.provider.id));
  if (!coverageIsUS) {
    for (const c of calls) {
      if ((US_ONLY_PROVIDER_IDS as readonly string[]).includes(c.provider.id)) {
        // PU4 — non-US coverage is a `permanent-unavailable` (the provider
        // CAN'T cover this lat/lng), not a transient. Retry won't help.
        providerStatuses.push({
          id: c.provider.id, name: c.provider.name,
          status: 'failed', error: 'non-us-coverage',
          failureMode: 'permanent-unavailable',
        });
        references.push({
          field: c.provider.id,
          source: `${c.provider.name} — skipped (site outside US coverage)`,
          retrievedAt: new Date().toISOString(),
        });
      }
    }
  }

  const results = await Promise.allSettled(
    callsAfterCoverage.map(async ({ provider, key }) => {
      // These providers consume a single ring. Do not let a multi-part study
      // silently become a centroid buffer or the first selected polygon.
      // FEMA flood supports full multi-polygon + hole intersections when the
      // caller supplies the complete boundaries (and any holes); if a caller
      // passes a multi-part/holed studyAreaScope without the matching rings,
      // fail closed rather than querying a centroid or single ring.
      const singleRingOnly = ['usfws-env', 'jurisdiction-boundaries', 'usgs-mrds', 'nps-nrhp', 'census-aiannh'];
      const femaMissingMultiPartRings =
        provider.id === 'fema-flood' &&
        Boolean(
          geo.studyAreaScope &&
            (!geo.boundaries ||
              geo.boundaries.length < geo.studyAreaScope.polygonCount ||
              (geo.studyAreaScope.hasHoles && !geo.boundaries.some(b => (b.holes?.length ?? 0) > 0))),
        );
      if (geo.studyAreaScope && (geo.studyAreaScope.polygonCount > 1 || geo.studyAreaScope.hasHoles)
        && (singleRingOnly.includes(provider.id) || femaMissingMultiPartRings)) {
        throw new Error('unsupported-study-area-geometry: Selected study area has multiple parts or holes; this provider requires a single polygon. Coverage is unavailable for this scope.');
      }
      // Each provider queries at its effective coordinate (CA3 flips
      // spatially-varying providers to the centroid; everyone else stays on
      // the geocoded address). The cache key and the `geo` context both
      // follow that choice.
      const qc = effectiveQueryCoord(provider.id, geo);
      return withCache(provider, qc.lat, qc.lng, key, geo);
    }),
  );
  throwIfAborted(abortSignal);

  let climate: ClimateData | null = null;
  let grid: GridData | null = null;
  let flood: FloodData | null = null;
  let seismic: SeismicData | null = null;
  let solar: SolarData | null = null;
  let wind: WindData | null = null;
  let environmental: EnvironmentalData | null = null;
  let electricity: ElectricityData | null = null;
  let naturalGas: NaturalGasData | null = null;
  let airQuality: AirQualityData | null = null;
  let connectivity: ConnectivityData | null = null;
  let parcel: ParcelData | null = null;
  let nrcReactors: NrcReactorData | null = null;
  let waterStress: WaterStressData | null = null;
  let siteResearch: SiteResearchData | null = null;
  let epaAttainment: EpaAttainmentData | null = null;
  let geotechnical: GeotechnicalData | null = null;
  // Content-Source Fixes Prompt 5 — slots for the three synthetic providers.
  let regulatory: AggregatedSiteData['regulatory'] = null;
  let jurisdiction: AggregatedSiteData['jurisdiction'] = null;
  let adamsDocuments: AggregatedSiteData['adamsDocuments'] = null;
  let siteProfileFromCentroid: AggregatedSiteData['siteProfileFromCentroid'] = null;
  let mineralResources: AggregatedSiteData['mineralResources'] = null;
  let culturalResources: AggregatedSiteData['culturalResources'] = null;
  // RD-1 (D) — refdata-only slots.
  let regulatedFacilities: AggregatedSiteData['regulatedFacilities'] = null;
  let protectedAreas: AggregatedSiteData['protectedAreas'] = null;
  let tribalLands: AggregatedSiteData['tribalLands'] = null;
  let waterResource: AggregatedSiteData['waterResource'] = null;

  for (let i = 0; i < results.length; i++) {
    throwIfAborted(abortSignal);
    const { provider } = callsAfterCoverage[i];
    const result = results[i];

    if (result.status === 'fulfilled') {
      const { data, sources, cached } = result.value;
      references.push(...sources);
      providerStatuses.push({ id: provider.id, name: provider.name, status: cached ? 'cached' : 'success' });

      switch (provider.id) {
        case 'open-meteo': climate = data as ClimateData; break;
        case 'usgs-seismic': seismic = data as SeismicData; break;
        case 'fema-flood': flood = data as FloodData; break;
        case 'nrcs-soil-survey': geotechnical = data as GeotechnicalData; break;
        case 'hifld-grid': grid = data as GridData; break;
        case 'nrel-solar': solar = data as SolarData; break;
        case 'nrel-wind': wind = data as WindData; break;
        case 'usfws-env': environmental = data as EnvironmentalData; break;
        case 'eia-electricity': electricity = data as ElectricityData; break;
        case 'eia-natural-gas': naturalGas = data as NaturalGasData; break;
        case 'airnow-aqi': airQuality = data as AirQualityData; break;
        case 'peeringdb-ix': connectivity = data as ConnectivityData; break;
        case 'nrc-reactors': nrcReactors = data as NrcReactorData; break;
        case 'wri-aqueduct': waterStress = data as WaterStressData; break;
        case 'site-research': siteResearch = data as SiteResearchData; break;
        case 'epa-attainment': epaAttainment = data as EpaAttainmentData; break;
        case 'regulatory-db':
          regulatory = data as AggregatedSiteData['regulatory'];
          break;
        case 'jurisdiction-boundaries':
          jurisdiction = data as AggregatedSiteData['jurisdiction'];
          break;
        case 'nrc-adams':
          adamsDocuments = (data as { documents: import('./nrcAdams').AdamsDocument[] })?.documents ?? null;
          break;
        case 'site-profile':
          siteProfileFromCentroid = data as AggregatedSiteData['siteProfileFromCentroid'];
          break;
        case 'usgs-mrds':
          mineralResources = data as AggregatedSiteData['mineralResources'];
          break;
        case 'nps-nrhp':
          culturalResources = data as AggregatedSiteData['culturalResources'];
          break;
        case 'epa-frs':
          regulatedFacilities = data as AggregatedSiteData['regulatedFacilities'];
          break;
        case 'usgs-waters':
          waterResource = data as AggregatedSiteData['waterResource'];
          break;
        case 'padus-protected':
          protectedAreas = data as AggregatedSiteData['protectedAreas'];
          break;
        case 'census-aiannh':
          tribalLands = data as AggregatedSiteData['tribalLands'];
          break;
      }
    } else {
      const error = result.reason instanceof Error ? result.reason.message : String(result.reason);
      // PU4 — classify the failure so renderers can distinguish
      // transient-failure (retry) / permanent-unavailable (no fix) /
      // misconfigured (admin attention required) instead of collapsing
      // all three into the same "Data unavailable" copy.
      const failureMode = classifyProviderFailure(provider.id, error);
      // PRR-4 — when the failure mode is auth-related, stamp the
      // canonical env-var name so the gap recommendation can name it
      // directly. Other modes leave envVar undefined.
      const envVar = (failureMode === 'auth-missing' || failureMode === 'auth-invalid')
        ? PROVIDER_ENV_VAR[provider.id]
        : undefined;
      providerStatuses.push({
        id: provider.id, name: provider.name,
        status: 'failed', error, failureMode,
        ...(envVar ? { envVar } : {}),
      });
      references.push({ field: provider.id, source: `${provider.name} — failed: ${error}`, retrievedAt: new Date().toISOString() });
    }
  }

  return {
    climate,
    grid,
    flood,
    seismic,
    solar,
    wind,
    environmental,
    electricity,
    naturalGas,
    airQuality,
    connectivity,
    parcel,
    nrcReactors,
    waterStress,
    siteResearch,
    epaAttainment,
    geotechnical,
    regulatory,
    jurisdiction,
    adamsDocuments,
    siteProfileFromCentroid,
    mineralResources,
    culturalResources,
    regulatedFacilities,
    protectedAreas,
    tribalLands,
    waterResource,
    references,
    providerStatuses,
  };
}
